import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import {capitalize} from 'lodash';
import * as Moment from 'moment';
import {BehaviorSubject, Observable} from 'rxjs';
import {retry, tap} from 'rxjs/operators';
import {
  IRequisitionSearch, RequisitionLabelViewModel, TransmissionModel,
} from '../../../shared/models/ocl';
import {FusionAuthService} from '../../../shared/services/fusion-auth';
import {UrlProviderService} from '../../../shared/services/general';
import {ObjectUtils, SECURED_STORE} from '../../../shared/utils';
import {AppState} from '../../../stores/app.state';
import {Requisition} from '../../features/requisitions/models/requisition';
import {RequisitionBillingInfo} from '../../features/requisitions/models/requisition-billing-infog';
import {MultipleRequisitionsSignature, RequisitionSignature, Signature} from '../models/signature';
import {BillTo} from '../models/billing/bill-type';
import {Document} from '../models/common/document';
import {TreeTest} from '../models/order-entry/test';
import { TestService } from './test.service';
import {BillingInformation} from "../models";

export interface IRequisitionSearchParams {
  externalFilters?: IRequisitionSearch;
  clientIds?: number[];
  patientIds?: number[];
  physicianIds?: number[];
  dataSource: BehaviorSubject<DataSource>;
}

@Injectable()
export class RequisitionService {

  constructor(
    private urlProvider: UrlProviderService,
    private httpClient: HttpClient,
    private store: Store<AppState>,
    private fusionAuthService: FusionAuthService,
    private testService: TestService,
    private router: Router
  ) {
  }

  public cancelRequisition(id: number, comment: string): Observable<Requisition> {
    const username = this.fusionAuthService.Profile.username;
    return this.httpClient.post<Requisition>(`${this.urlProvider.ApiUrl}/v2/requisitions/${id}/cancel`, {
      comment,
      username,
    });
  }

  public updateAdvancedBeneficiaryNotice(id: number): Observable<Requisition> {
    const username = this.fusionAuthService.Profile.username;
    return this.httpClient.put<Requisition>(`${this.urlProvider.ApiUrl}/v2/requisitions/${id}/abn-status`, {
      username,
    });
  }

  public requisitionStore(params: IRequisitionSearchParams): CustomStore {
    const loadParams = {
      ...params.externalFilters,
      startDate: Moment(params.externalFilters?.startDate, 'MM/DD/YYYY').format('MM/DD/YYYY'),
      endDate: Moment(params.externalFilters?.endDate, 'MM/DD/YYYY').format('MM/DD/YYYY'),
      client: params.externalFilters?.client ? params.externalFilters.client.id : undefined,
      unit: params.externalFilters?.unit ? params.externalFilters.unit.id : undefined,
      patient: params.externalFilters?.patient ? params.externalFilters.patient.id : undefined,
      clientIds: params.clientIds,
      patientIds: params.patientIds,
      physicianIds: params.physicianIds,
    };

    return SECURED_STORE({
      routerService: this.router,
      dataSource: params.dataSource,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      loadParams,
      loadUrl: `${this.urlProvider.ApiUrl}/v2/requisitions/remote-operations`
    });
  }

  public requisitionSignature(requisitionId: number): Observable<RequisitionSignature> {
    return this.httpClient.get<RequisitionSignature>(`${this.urlProvider.ApiUrl}/v2/requisitions/${requisitionId}/signature`).pipe(retry(2));
  }

  public getRequisition(requisitionId: number): Observable<Requisition> {
    return this.httpClient.get<Requisition>(`${this.urlProvider.ApiUrl}/v2/requisitions/${requisitionId}`)
      .pipe(
        tap(req => {
          req.treeTests = req.tests as TreeTest[];
          req.treeTests = [];
          for (const test of req.tests ? req.tests : []) {
            if (!test.test) continue;
            const treeTest = new TreeTest();
            treeTest.name = test.test.name;
            treeTest.alternateNames = test.test.alternateNames;
            treeTest.code = test.test.code;
            treeTest.type = test.test.type;
            treeTest.isConfidential = test.isConfidential;
            treeTest.id = test.test.id;
            treeTest.children = test.test.children;
            // @ts-ignore
            treeTest.parentId = test.parentTestId;
            req.treeTests.push(treeTest);
          }
          //Sentry.setContext('loaded-req', {...req});
        }),
        retry(2));
  }

  public getRequisitionBillingInformation(requisitionId: number) {
    return this.httpClient.get<RequisitionBillingInfo[]>(`${this.urlProvider.ApiUrl}/v2/requisitions/${requisitionId}/billing-info`)
      .pipe(retry(2));
  }

  public getRequisitionDocuments(requisitionId: number) {
    return this.httpClient.get<Document[]>(`${this.urlProvider.ApiUrl}/v2/requisitions/${requisitionId}/documents`)
      .pipe(retry(2));
  }

  public getRequisitionSignature(requisitionId: number) {
    return this.httpClient.get<RequisitionSignature>(`${this.urlProvider.ApiUrl}/v2/requisitions/${requisitionId}/signature`)
      .pipe(retry(2));
  }

  public unsign(id: number): Observable<null> {
    return this.httpClient.post<null>(`${this.urlProvider.ApiUrl}/v2/requisitions/${id}/unsign`, {});
  }

  public downloadRequisitionDocuments(requisitionId: number, documentName: string) {
    const url = `${this.urlProvider.ApiUrl}/v2/requisitions/${requisitionId}/documents/download?name=${documentName}`;
    const httpOptions = {
      headers: new HttpHeaders(),
      responseType: 'blob' as 'json',
    };
    httpOptions.headers.append('Authorization', this.fusionAuthService.getToken());
    httpOptions.headers.append('X-Authorization', this.fusionAuthService.getToken());

    return this.httpClient.get<any>(url, httpOptions);
  }

  public removeRequisitionDocument(requisitionId: number, documentId: number) {
    const url = `${this.urlProvider.ApiUrl}/v2/requisitions/${requisitionId}/documents/${documentId}`;

    return this.httpClient.delete<any>(url);
  }

  public signRequisitions(signature: MultipleRequisitionsSignature): Observable<null> {
    signature = ObjectUtils.capitalize(signature) as MultipleRequisitionsSignature;
    return this.httpClient.post<null>(`${this.urlProvider.ApiUrl}/v2/requisitions/sign-multiple`, signature);
  }

  public uploadDocument(requisitionId: number, fileBase64: string, fileName: string): Observable<any> {
    return this.httpClient.post<any>(
      `${this.urlProvider.ApiUrl}/v2/requisitions/${requisitionId}/documents`, {
        fileBase64,
        fileName
      }, {
        reportProgress: true
      }
    );
  }

  public create(requisition: Requisition): Observable<Requisition> {
    requisition = ObjectUtils.capitalize(requisition) as Requisition;
    return this.httpClient.post<Requisition>(`${this.urlProvider.ApiUrl}/v2/requisitions`, requisition);
  }

  public update(requisition: Requisition): Observable<Requisition> {
    requisition = ObjectUtils.capitalize(requisition) as Requisition;
    return this.httpClient.post<Requisition>(`${this.urlProvider.ApiUrl}/v2/requisitions/${requisition.id}`, requisition);
  }

  public saveMedications(id: number, medicationIds: number[] = []): Observable<null> {
    return this.httpClient.post<null>(`${this.urlProvider.ApiUrl}/v2/requisitions/${id}/save-medications`, medicationIds);
  }

  public saveDiagnoses(id: number, diagnosesIds: number[] = []): Observable<null> {
    return this.httpClient.post<null>(`${this.urlProvider.ApiUrl}/v2/requisitions/${id}/save-diagnoses`, diagnosesIds);
  }

  public saveBillingInformation(id: number, billingInformation: RequisitionBillingInfo[] = []): Observable<null> {
    billingInformation.forEach(b => b.requisitionId = id);
    return this.httpClient.post<null>(`${this.urlProvider.ApiUrl}/v2/requisitions/${id}/save-billing-info`, {billingInfo: billingInformation});
  }

  public changeBillTo(billTo: string, requisitionsIds: number[]) {
    // @ts-ignore
    let url = `/v2/requisitions/change-bill-to?billTo=${BillTo[capitalize(billTo)]}&`;
    const ids: string[] = [];
    requisitionsIds.map(x => {
      ids.push(`ids=${x}`);
    });

    if (ids.length > 0) {
      url += ids.join('&');
    }

    return this.httpClient.put(`${this.urlProvider.ApiUrl}${url}`, {reportProgress: true});
  }

  public sendTransmission(transmission: TransmissionModel): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this.urlProvider.ApiUrl}/v2/requisitions/transmission`, transmission);
  }

  public getLabels(id: number): Observable<RequisitionLabelViewModel[]> {
    return this.httpClient.get<RequisitionLabelViewModel[]>(`${this.urlProvider.ApiUrl}/v2/requisitions/${id}/labels`)
      .pipe(
        retry(2),
        tap(labels => {
          labels.forEach((label, index) => {
            label.id = index;
            return label;
          });
        })
      );
  }
}
